import React, { Fragment, useMemo, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { priorityPillClass, statusDotClass, statusPillClass, tableMoney } from './planHelpers';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

/**
 * IB-700 (v14) — the redesigned Plan tab Gantt. A props-driven, self-contained renderer (no
 * self-fetch) so IB-702's department Gantt can reuse it. It renders the SAME filtered/grouped item
 * set as the register and Kanban.
 *
 * Props:
 *   - groups: the grouped payload `[{ category|department, items: [...] }]`.
 *   - currencySymbol: budget currency symbol for the Value column.
 *
 * The month columns (`dates`) are derived CLIENT-SIDE from the union of every item's target dates
 * (min → max month span), mirroring how the legacy chart placed S/R/D/C/CC markers by matching
 * `YYYY-MM`. The current month is highlighted.
 */

// The five S/R/D/C/CC date markers + their legend labels, reused verbatim from the legacy chart so
// the glyphs render identically (FontAwesome `fa fa-s/r/d/c` icons).
const MARKERS = [
    { key: 'target_scope_issue_to_market_date', icon: 'scope_issue_to_market', label: $t('Scope Issue to Market') },
    { key: 'target_response_due_date', icon: 'response_due_date', label: $t('Response Due Date') },
    { key: 'target_decision_date', icon: 'decision_date', label: $t('Decision Date') },
    { key: 'target_contract_award_date', icon: 'contract_awarded_date', label: $t('Contract Awarded Date') },
    { key: 'target_contract_completion_date', icon: 'contract_completion_date', label: $t('Contract Completion Date') },
];

const MarkerIcon = ({ name }) => {
    switch (name) {
        case 'scope_issue_to_market':
            return <i className="fa fa-sm fa-s" />;
        case 'response_due_date':
            return <i className="fa fa-sm fa-r" />;
        case 'decision_date':
            return <i className="fa fa-sm fa-d" />;
        case 'contract_awarded_date':
            return <i className="fa fa-sm fa-c" />;
        case 'contract_completion_date':
            return (
                <span>
                    <i className="fa fa-sm fa-c" />
                    <i className="fa fa-sm fa-c" />
                </span>
            );
        default:
            return null;
    }
};

/** Heading for a grouped payload row — works whether grouped by category or department. */
const groupHeading = (group) => group.category ?? group.department ?? Object.entries(group).find(([key]) => key !== 'items')?.[1] ?? '';

const GanttGroup = ({ group, dates, currentMonth, currencySymbol, showDescription }) => {
    const [open, setOpen] = useState(true);
    const heading = groupHeading(group);
    const leftColumns = showDescription ? 6 : 5;

    return (
        <Fragment>
            <tr className="tw-bg-charcoal-10">
                <td colSpan={leftColumns + dates.length} className="tw-px-3 tw-py-2">
                    <button
                        type="button"
                        onClick={() => setOpen((prev) => !prev)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-charcoal"
                    >
                        {open ? <ChevronUpIcon className="tw-w-2 tw-h-2" /> : <ChevronDownIcon className="tw-w-2 tw-h-2" />}
                        {heading}
                    </button>
                </td>
            </tr>
            {open &&
                (group.items ?? []).map((item) => {
                    const statusTitle = item.status?.title || 'Not Started';

                    return (
                        <tr key={item.id} className="tw-border-t tw-border-grey hover:tw-bg-charcoal-10">
                            <td className="tw-px-3 tw-py-3 tw-align-top tw-font-medium tw-text-charcoal">{item.name}</td>
                            {showDescription && (
                                <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">
                                    <span className="tw-line-clamp-2">{item.description || '—'}</span>
                                </td>
                            )}
                            <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{tableMoney(currencySymbol, item.value)}</td>
                            <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{item.duration || $t('No Data')}</td>
                            <td className="tw-px-3 tw-py-3 tw-align-top">
                                {item.priority_label && (
                                    <span
                                        className={classNames(
                                            'tw-inline-flex tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold',
                                            priorityPillClass(item.priority_label),
                                        )}
                                    >
                                        {item.priority_label}
                                    </span>
                                )}
                            </td>
                            <td className="tw-px-3 tw-py-3 tw-align-top">
                                <span
                                    className={classNames(
                                        'tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold',
                                        statusPillClass(statusTitle),
                                    )}
                                >
                                    <span className={classNames('tw-h-2 tw-w-2 tw-rounded-full tw-shrink-0', statusDotClass(statusTitle))} />
                                    {statusTitle}
                                </span>
                            </td>
                            {dates.map((date) => {
                                const cellMonth = moment(date, 'YYYY-MM-DD').format('YYYY-MM');

                                return (
                                    <td
                                        key={item.id + '_' + date}
                                        className={classNames('tw-px-3 tw-py-3 tw-align-middle tw-text-center tw-text-charcoal', {
                                            'tw-bg-blue-10': cellMonth === currentMonth,
                                        })}
                                    >
                                        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-1">
                                            {MARKERS.map(
                                                ({ key, icon }) =>
                                                    item[key] &&
                                                    moment(item[key], 'YYYY-MM-DD').format('YYYY-MM') === cellMonth && <MarkerIcon key={icon} name={icon} />,
                                            )}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
        </Fragment>
    );
};

const PlanGantt = ({ groups, currencySymbol, showDescription = false }) => {
    const currentMonth = moment().format('YYYY-MM');

    // Derive the month columns from the union of every item's target dates (min → max month).
    const dates = useMemo(() => {
        const months = [];
        (groups ?? []).forEach((group) => {
            (group.items ?? []).forEach((item) => {
                MARKERS.forEach(({ key }) => {
                    const raw = item[key];
                    if (raw) {
                        const parsed = moment(raw, 'YYYY-MM-DD');
                        if (parsed.isValid()) {
                            months.push(parsed.startOf('month'));
                        }
                    }
                });
            });
        });

        if (!months.length) {
            return [];
        }

        const min = moment.min(months).clone();
        const max = moment.max(months).clone();
        const span = [];
        for (const cursor = min; cursor.isSameOrBefore(max, 'month'); cursor.add(1, 'month')) {
            span.push(cursor.clone().format('YYYY-MM-DD'));
        }
        return span;
    }, [groups]);

    const hasItems = (groups ?? []).some((group) => (group.items ?? []).length);

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* S/R/D/C/CC legend */}
            <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-x-4 tw-gap-y-2">
                {MARKERS.map(({ icon, label }) => (
                    <div key={icon} className="tw-flex tw-items-center tw-gap-x-2 tw-whitespace-nowrap">
                        <span className="tw-basis-6 tw-text-center tw-text-charcoal">
                            <MarkerIcon name={icon} />
                        </span>
                        <span className="tw-text-sm tw-text-charcoal-75">{label}</span>
                    </div>
                ))}
            </div>

            {!hasItems ? (
                <p className="tw-py-6 tw-text-center tw-text-sm tw-text-charcoal-50">{$t('No procurements')}</p>
            ) : (
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-left">
                        <thead>
                            <tr className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                                <th className="tw-px-3 tw-pb-2">{$t('Procurement')}</th>
                                {showDescription && <th className="tw-px-3 tw-pb-2">{$t('Description')}</th>}
                                <th className="tw-px-3 tw-pb-2">{$t('Value')}</th>
                                <th className="tw-px-3 tw-pb-2">{$t('Duration')}</th>
                                <th className="tw-px-3 tw-pb-2">{$t('Priority')}</th>
                                <th className="tw-px-3 tw-pb-2">{$t('Status')}</th>
                                {dates.map((date) => {
                                    const month = moment(date, 'YYYY-MM-DD');

                                    return (
                                        <th
                                            key={date}
                                            className={classNames('tw-px-3 tw-pb-2 tw-text-center tw-align-middle', {
                                                'tw-bg-blue-10 tw-text-blue': month.format('YYYY-MM') === currentMonth,
                                            })}
                                        >
                                            {month.format('MM')}
                                            <br />
                                            {month.format('YYYY')}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {(groups ?? []).map((group) => (
                                <GanttGroup
                                    key={groupHeading(group)}
                                    group={group}
                                    dates={dates}
                                    currentMonth={currentMonth}
                                    currencySymbol={currencySymbol}
                                    showDescription={showDescription}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PlanGantt;
